/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ChangeEvent } from 'react';
import { Grid, Card, CardHeader, CardContent, FormGroup, makeStyles, TextField } from '@material-ui/core';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Offset, OffsetConnection } from '../../utils/types';
import Offsets from './Offsets';
import DayjsUtils from '@date-io/dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const useStyles = makeStyles((theme) => ({
	inputField: {
		paddingBottom: theme.spacing(2)
	}
}));

export const GameDetailsEditor: React.FC<GameDetailsEditorProps> = (props) => {
	const { id, onChange, onOffsetsChange, sport, startTime, title, offsets } = props;
	const classes = useStyles();

	const handleStartTimeChange = (date: dayjs.Dayjs | null): void => {
		if (date) {
			const value = date.toISOString();
			const event: DateChangeEvent = {
				target: { name: 'startTime', value }
			};
			onChange && onChange(event);
		}
	};

	const handleOffsetAdd = async (data: Offset): Promise<void> => {
		const newOffsets = offsets?.items ? [...offsets.items, data] : [data];
		onOffsetsChange({ items: newOffsets });
	};

	const handleOffsetDelete = async (oldData: Offset): Promise<void> => {
		const dataDelete = offsets?.items ? [...offsets.items] : [];
		const dataDeleteSize = dataDelete.length;
		const index = dataDelete.indexOf(oldData);
		dataDelete.splice(index, 1);
		dataDeleteSize > 1 ? onOffsetsChange({ items: [...dataDelete] }) : onOffsetsChange({ items: null });
	};

	const handleOffsetUpdate = async (newData: Offset, oldData?: Offset): Promise<void> => {
		if (oldData) {
			const dataUpdate = offsets?.items ? [...offsets.items] : [];
			const index = dataUpdate.indexOf(oldData);
			dataUpdate[index] = newData;
			onOffsetsChange({ items: [...dataUpdate] });
		}
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} lg={6}>
				<Card>
					<CardHeader title='Game details' />
					<CardContent>
						<FormGroup>
							<TextField id='id' label='ID' name='id' className={classes.inputField} value={id} disabled fullWidth />
							<TextField id='title' label='Title' name='title' className={classes.inputField} value={title} onChange={onChange} fullWidth />
							<TextField id='sport' label='Sport' name='sport' className={classes.inputField} value={sport} onChange={onChange} fullWidth />
							<MuiPickersUtilsProvider utils={DayjsUtils}>
								<DateTimePicker
									id='startTime'
									label='Start time'
									name='startTime'
									value={dayjs(startTime?.toString()).utc().local()}
									onChange={handleStartTimeChange}
								/>
							</MuiPickersUtilsProvider>
						</FormGroup>
					</CardContent>
				</Card>
			</Grid>
			{sport?.toLowerCase() === 'football' && (
				<Grid item xs={12} lg={12}>
					<Offsets
						offsets={offsets?.items ?? []}
						onRowAdd={handleOffsetAdd}
						onRowDelete={handleOffsetDelete}
						onRowUpdate={handleOffsetUpdate}
						sport={sport}
					/>
				</Grid>
			)}
			{sport?.toLowerCase() === 'americanfootball' && (
				<Grid item xs={12} lg={12}>
					<Offsets
						offsets={offsets?.items ?? []}
						onRowAdd={handleOffsetAdd}
						onRowDelete={handleOffsetDelete}
						onRowUpdate={handleOffsetUpdate}
						sport={sport}
					/>
				</Grid>
			)}
		</Grid>
	);
};

type GameDetailsEditorProps = {
	id: string;
	onChange?: (event: ChangeEvent<HTMLInputElement> | DateChangeEvent) => void;
	title: string | null;
	sport: string | null;
	startTime: string | null;
	offsets?: OffsetConnection | null;
	onOffsetsChange: (assetConnection: OffsetConnection) => void;
	idEditable?: boolean;
};

export type DateChangeEvent = {
	target: {
		name: string;
		value: string;
	};
};

export default GameDetailsEditor;
